import React, { useState, useEffect, useRef } from 'react';
import {
    Paper, Grid, FormControl,
    Typography, FormLabel, FormGroup, FormControlLabel,
    Checkbox, Button, Box,
    Collapse, IconButton, useMediaQuery, useTheme
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DateRangeFilter from './DateRangeFilter';
import StockAutocompleteInput from '../StockAutocompleteInput/StockAutocompleteInput';
import { config } from '../../Constants';
import { PRODUCT_CATEGORIES, CATEGORY_LABELS } from '../../utils/productCategories';

const URL = config.url;
const DEFAULT_CATEGORIES = PRODUCT_CATEGORIES;

const StockFilters = ({ 
    filters, 
    onFiltersChange, 
    onClearFilters, 
    onSearch, 
    initialSearchValue = '',
    suggestionFilters,
    disabled = false,
    showManufacturer = true,
    showCategory = true,
    showPrice = true,
    showAvailability = true,
    showOnSale = true,
    showDateRange = false,
    layout = 'bar'
}) => {
    const seriesOptions = [
        'RTX 5060',
        'RTX 5070',
        'RTX 5080',
        'RTX 5090',
        'RX 9060',
        'RX 9070'
    ];
    const theme = useTheme();
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
    const isSidebar = layout === 'sidebar';
    const [searchValue, setSearchValue] = useState('');
    const [expanded, setExpanded] = useState(() => !(isSidebar && isMobileOrTablet));
    const [categoryOptions, setCategoryOptions] = useState(DEFAULT_CATEGORIES);
    const debounceTimerRef = useRef(null);
    
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        setExpanded(!(isSidebar && isMobileOrTablet));
    }, [isSidebar, isMobileOrTablet]);

    useEffect(() => {
        setSearchValue(initialSearchValue || '');
    }, [initialSearchValue]);

    useEffect(() => {
        let isMounted = true;

        const loadMetadata = async () => {
            if (!showCategory) {
                return;
            }

            try {
                const response = await fetch(`${URL}/api/v1/stocks/filters`);
                if (!response.ok) {
                    return;
                }

                const payload = await response.json();
                const metadata = payload?.data || {};
                if (!isMounted) {
                    return;
                }

                const nextCategories = Array.isArray(metadata.categories) && metadata.categories.length > 0
                    ? metadata.categories
                    : DEFAULT_CATEGORIES;

                setCategoryOptions(nextCategories);
            } catch (_error) {
                // Keep fallback options when metadata is unavailable.
            }
        };

        loadMetadata();

        return () => {
            isMounted = false;
        };
    }, [showCategory]);
    
    const handleSearchChange = (newValue) => {
        setSearchValue(newValue);

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            if (newValue.length >= 2 || newValue.length === 0) {
                onSearch(newValue);
            }
        }, 500);

    };
    
    const handleCategoryToggle = (category) => (e) => {
        const currentCategories = Array.isArray(filters.categories) ? filters.categories : [];
        const nextCategories = new Set(currentCategories);

        if (e.target.checked) {
            nextCategories.add(category);
        } else {
            nextCategories.delete(category);
        }

        const updatedCategories = Array.from(nextCategories);
        const nextFilters = {
            ...filters,
            categories: updatedCategories.length ? updatedCategories : undefined
        };

        const shouldKeepSeries = updatedCategories.length === 0 || updatedCategories.includes('GPU');
        if (!shouldKeepSeries) {
            nextFilters.series = undefined;
        }

        onFiltersChange(nextFilters);
    };

    const handleSeriesChange = (seriesValue) => (e) => {
        const currentSeries = Array.isArray(filters.series) ? filters.series : [];
        const nextSeries = new Set(currentSeries);

        if (e.target.checked) {
            nextSeries.add(seriesValue);
        } else {
            nextSeries.delete(seriesValue);
        }

        const updatedSeries = Array.from(nextSeries);

        onFiltersChange({
            ...filters,
            series: updatedSeries.length ? updatedSeries : undefined
        });
    };

    const handleInStoreChange = (e) => {
        onFiltersChange({
            ...filters,
            inStoreAvailable: e.target.checked || undefined
        });
    };

    const handleOnlineChange = (e) => {
        onFiltersChange({
            ...filters,
            onlineAvailable: e.target.checked || undefined
        });
    };

    const handleAnyAvailableChange = (e) => {
        if (e.target.checked) {
            onFiltersChange({
                ...filters,
                anyAvailable: true,
                inStoreAvailable: undefined,
                onlineAvailable: undefined
            });
        } else {
            onFiltersChange({
                ...filters,
                anyAvailable: undefined
            });
        }
    };

    const handleOnSaleChange = (e) => {
        onFiltersChange({
            ...filters,
            onSale: e.target.checked || undefined
        });
    };

    const handleDateFromChange = (newDate) => {
        onFiltersChange({
            ...filters,
            releaseDateFrom: newDate
        });
    };

    const handleDateToChange = (newDate) => {
        onFiltersChange({
            ...filters,
            releaseDateTo: newDate
        });
    };
    
    const handleClearAll = () => {
        setSearchValue('');
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        onSearch('');
        onClearFilters();
    };

    const activeFilterCount = Object.keys(filters).filter((key) => {
        const value = filters[key];
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return value !== undefined && value !== null && value !== '';
    }).length;

    const isMobileHeaderToggle = isSidebar && isMobileOrTablet;
    const isDesktopSidebar = isSidebar && !isMobileOrTablet;
    // Desktop sidebar: tighter checkbox rows so the whole panel fits a laptop window.
    const denseGroupSx = isDesktopSidebar
        ? { '& .MuiCheckbox-root': { py: 0.5 } }
        : {};
    const shouldShowSeriesFilters = !Array.isArray(filters.categories)
        || filters.categories.length === 0
        || filters.categories.includes('GPU');

    const hasAnythingToClear = activeFilterCount > 0 || Boolean(searchValue);

    const handleHeaderToggle = () => {
        if (!isMobileHeaderToggle) {
            return;
        }
        setExpanded((prev) => !prev);
    };

    return (
        <Paper 
            elevation={2} 
            sx={{ 
                p: 2,
                mb: isSidebar ? 0 : 2,
                // Desktop sidebar: never taller than the window below the sticky header (116px)
                // plus breathing room, so every filter is reachable without scrolling the page.
                ...(isDesktopSidebar ? {
                    maxHeight: 'calc(100vh - 144px)',
                    overflowY: 'auto',
                    overscrollBehavior: 'contain'
                } : {})
            }}
        >
            <Box
                onClick={handleHeaderToggle}
                onKeyDown={(event) => {
                    if (!isMobileHeaderToggle) {
                        return;
                    }
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleHeaderToggle();
                    }
                }}
                role={isMobileHeaderToggle ? 'button' : undefined}
                tabIndex={isMobileHeaderToggle ? 0 : undefined}
                aria-expanded={isMobileHeaderToggle ? expanded : undefined}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: expanded ? 2 : 0,
                    cursor: isMobileHeaderToggle ? 'pointer' : 'default'
                }}
            >
                <FilterListIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                    Filter Products
                    {isSidebar && activeFilterCount > 0 && (
                        <Box component="span" aria-label={`${activeFilterCount} active`} sx={{
                            ml: 1, px: 0.9, py: 0.1, borderRadius: 999, fontSize: '0.75rem', fontWeight: 700,
                            bgcolor: 'primary.main', color: 'primary.contrastText', verticalAlign: 'middle'
                        }}>
                            {activeFilterCount}
                        </Box>
                    )}
                </Typography>
                {isSidebar && hasAnythingToClear && (
                    <Button
                        size="small"
                        onClick={(event) => {
                            event.stopPropagation();
                            handleClearAll();
                        }}
                        onKeyDown={(event) => event.stopPropagation()}
                        disabled={disabled}
                        sx={{ minWidth: 0, textTransform: 'none' }}
                    >
                        Clear
                    </Button>
                )}
                <IconButton 
                    size="small" 
                    tabIndex={-1}
                    sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                >
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            <Collapse in={expanded} timeout="auto">
                <Grid
                    container
                    spacing={2}
                    alignItems={isSidebar ? 'stretch' : 'flex-start'}
                    direction={isSidebar ? 'column' : 'row'}
                >
                    {/* Search - Always visible */}
                    <Grid item xs={12} md={isSidebar ? 12 : showDateRange ? 12 : 3}>
                        <StockAutocompleteInput
                            value={searchValue}
                            onValueChange={handleSearchChange}
                            onSubmit={(term) => {
                                if (debounceTimerRef.current) {
                                    clearTimeout(debounceTimerRef.current);
                                }
                                onSearch(term);
                            }}
                            disabled={disabled}
                            size="medium"
                            suggestionFilters={suggestionFilters || filters}
                        />
                    </Grid>

                    {/* In stock - first, because it is the filter people use most */}
                    {showAvailability && !showDateRange && (
                        <Grid item xs={12} md={isSidebar ? 12 : 3}>
                            <FormControl component="fieldset" disabled={disabled} fullWidth>
                                <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                                    In stock
                                </FormLabel>
                                <FormGroup
                                    row
                                    sx={{
                                        flexWrap: isSidebar ? 'wrap' : 'nowrap',
                                        columnGap: isSidebar ? 1 : 0.5,
                                        rowGap: 0,
                                        ...denseGroupSx
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox 
                                                checked={filters.anyAvailable || false}
                                                onChange={handleAnyAvailableChange}
                                                disabled={disabled}
                                                size="small"
                                            />
                                        }
                                        label={<Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Anywhere</Typography>}
                                        sx={{ mr: 0.5 }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox 
                                                checked={filters.inStoreAvailable || false}
                                                onChange={handleInStoreChange}
                                                disabled={disabled || filters.anyAvailable}
                                                size="small"
                                            />
                                        }
                                        label={<Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>In&nbsp;Store</Typography>}
                                        sx={{ mr: 0.5 }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox 
                                                checked={filters.onlineAvailable || false}
                                                onChange={handleOnlineChange}
                                                disabled={disabled || filters.anyAvailable}
                                                size="small"
                                            />
                                        }
                                        label={<Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Online</Typography>}
                                    />
                                </FormGroup>
                            </FormControl>
                        </Grid>
                    )}

                    {/* Current Deals - Only when enabled */}
                    {showOnSale && !showDateRange && (
                        <Grid item xs={12} md={isSidebar ? 12 : 2}>
                            <FormControl component="fieldset" disabled={disabled} fullWidth>
                                <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                                    Current Deals
                                </FormLabel>
                                <FormGroup row sx={{ gap: 0.5, ...denseGroupSx }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={filters.onSale || false}
                                                onChange={handleOnSaleChange}
                                                disabled={disabled}
                                                size="small"
                                            />
                                        }
                                        label={<Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>On&nbsp;Sale</Typography>}
                                        sx={{ mr: 0.5 }}
                                    />
                                </FormGroup>
                            </FormControl>
                        </Grid>
                    )}

                    {showCategory && !showDateRange && (
                        <Grid item xs={12} md={isSidebar ? 12 : 2}>
                            <FormControl component="fieldset" disabled={disabled} fullWidth>
                                <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                                    Category
                                </FormLabel>
                                <FormGroup sx={denseGroupSx}>
                                    {categoryOptions.map((category) => (
                                        <FormControlLabel
                                            key={category}
                                            control={
                                                <Checkbox
                                                    checked={Array.isArray(filters.categories)
                                                        ? filters.categories.includes(category)
                                                        : false}
                                                    onChange={handleCategoryToggle(category)}
                                                    disabled={disabled}
                                                    size="small"
                                                />
                                            }
                                            label={<Typography variant="body2">{CATEGORY_LABELS[category] || category}</Typography>}
                                        />
                                    ))}
                                </FormGroup>
                            </FormControl>
                        </Grid>
                    )}

                    {/* GPU Series - Only when enabled */}
                    {!showDateRange && shouldShowSeriesFilters && (
                        <Grid item xs={12} md={isSidebar ? 12 : 3}>
                            <FormControl component="fieldset" disabled={disabled} fullWidth>
                                <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                                    GPU Series
                                </FormLabel>
                                <FormGroup
                                    sx={isSidebar
                                        ? { ...denseGroupSx, display: 'grid', gridTemplateColumns: '1fr 1fr' }
                                        : undefined}
                                >
                                    {seriesOptions.map((series) => (
                                        <FormControlLabel
                                            key={series}
                                            control={
                                                <Checkbox
                                                    checked={Array.isArray(filters.series) ? filters.series.includes(series) : false}
                                                    onChange={handleSeriesChange(series)}
                                                    disabled={disabled}
                                                    size="small"
                                                />
                                            }
                                            label={<Typography variant="body2">{series}</Typography>}
                                        />
                                    ))}
                                </FormGroup>
                            </FormControl>
                        </Grid>
                    )}

                    {/* Date Range - Only on dates page */}
                    {showDateRange && (
                        <Grid item xs={12} md={12}>
                            <DateRangeFilter
                                dateFrom={filters.releaseDateFrom}
                                dateTo={filters.releaseDateTo}
                                onDateFromChange={handleDateFromChange}
                                onDateToChange={handleDateToChange}
                                disabled={disabled}
                            />
                        </Grid>
                    )}

                    {/* Clear All Button (the sidebar has Clear in its header instead) */}
                    {!isSidebar && (
                    <Grid item xs={12} md={2}>
                        <Button 
                            variant="outlined" 
                            onClick={handleClearAll}
                            fullWidth
                            disabled={disabled || (activeFilterCount === 0 && !searchValue)}
                            startIcon={<ClearIcon />}
                            size="small"
                        >
                            Clear All
                        </Button>
                    </Grid>
                    )}
                </Grid>
            </Collapse>
        </Paper>
    );
};

export default StockFilters;
